import { IconButton, Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { FormCheckbox, FormInputText } from 'components/common/Form/Fields';
import { FormTrackUseTypeSelect } from 'components/common/TrackUseTypeSelect';
import { CustomGridItem, InputFormat } from 'components/styleguide';
import { getIn, useField, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { COMMON_TRACK_USE_TYPES } from 'utils/constants';
import { TIME_FORMATS } from 'utils/format';
import { useNumberFormat, useRootTranslation } from 'utils/hooks';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import c from 'ramda/src/compose';
import filter from 'ramda/src/filter';
import flatten from 'ramda/src/flatten';
import map from 'ramda/src/map';
import dissoc from 'ramda/src/dissoc';
import { Autocomplete, TextField } from '@mui/material';
import { refetchCueContainers } from 'redux-core/cueContainers/actions';
import { updateClearancesCues } from '../../../../../../../redux-core/clearances/services';
import { moveTrack } from '../../../../../../../redux-core/tracks/actions';
import useStyles from './styles';
import enhancer from './enhancer';

export const REMOVE_GUTTER = 40;

const ScenesContainerBase = ({
  cueContainers,
  currentContainerId,
  trackId,
  cueRef,
  qTrackId,
  refetch,
  placeholder,
}) => {
  /** The group options come from the full subproject group list, so memoize them — otherwise
   * every CueRow rebuilds this array on each Formik keystroke. */
  const options = useMemo(
    () => cueContainers.map(({ id, title, number }) => ({ id, name: `${number || ''} - ${title}` })),
    [cueContainers],
  );
  const [selected, setSelected] = useState(options.filter((i) => i.id === currentContainerId)[0] || options[0]);
  /** A move is create-then-delete across two requests, and this row's cue only refreshes once the
   * drawer refetches. Letting a second move start before that lands makes the delete target the
   * cue the first move already deleted, orphaning the one it created. Moves are serialised. */
  const [isMoving, setIsMoving] = useState(false);
  const dispatch = useDispatch();

  const saveForm = async ({ cues: cuesRaw }) => {
    const cues = c(
      filter(({ id, deleted }) => !(id === null && deleted)) /** Remove NEW cues that were deleted */,
      flatten,
      map((cueIterable) =>
        cueIterable.cueContainerIds.map((id) => ({
          /** cue.cueContainerIds is an array of the containers the cue is at, create a new cue for each container */
          ...dissoc('cueContainerIds', cueIterable),
          cueContainerId: id,
          actualUseDuration: cueIterable.actualUseDuration || null,
          id: /newCue/.test(cueIterable.id)
            ? null
            : cueIterable.id /** If regex pass, it means it is a NEW cue and shouldn't carry an id */,
        })),
      ),
    )(cuesRaw);
    await updateClearancesCues({ trackId, cues });
  };

  const onChange = async (cueContainerFrom, cueContainerTo, toSceneName) => {
    /** Read the latest cue from the ref so this component can stay memoized (the cue object is
     * rebuilt on every parent render and would otherwise defeat React.memo). */
    const cue = cueRef.current;
    setIsMoving(true);
    try {
      await dispatch(moveTrack({
        cueContainerId: cueContainerTo,
        durationFull: cue.requestedUseDurationFull,
        duration: cue.requestedUseDuration,
        qwireTracksId: qTrackId,
        useType: cue.requestedUseType,
        actualUseType: cue.actualUseType,
        actualUseDuration: cue.actualUseDuration,
        qclearClearanceId: cue.qclearClearanceId,
        isMoveUsage: true,
        toSceneName,
      }, { isAdding: true }));
      await saveForm({ cues: [{ ...cue, deleted: true }] });
      await dispatch(refetchCueContainers());
      await refetch();
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Autocomplete
      fullWidth
      key={1}
      id="select"
      value={selected}
      disabled={isMoving}
      onChange={(_, nextValue) => {
        onChange(selected.id, nextValue.id, nextValue.name.split('-')[1].trim());
        setSelected(nextValue);
      }}
      options={options}
      isOptionEqualToValue={(option, optionValue) => option?.id === optionValue?.id}
      getOptionLabel={(option) => option?.name ?? ''}
      renderInput={(params) => (
        /* eslint-disable-next-line react/jsx-props-no-spreading, no-restricted-globals */
        <TextField fullWidth {...params} name={name} placeholder={placeholder} />
      )}
      ListboxProps={{ width: 200 }}
    />
  );
};

/** Memoized: the group select is an MUI Autocomplete and re-rendering it on every keystroke of
 * any cue field (across all clearances) was a major source of the drawer lag. All props are
 * referentially stable (cue is passed via a ref), so it only re-renders when its own group changes. */
export const ScenesContainer = memo(ScenesContainerBase);

/** Memoized so a keystroke in another duration/use-type field of the form doesn't re-render this
 * NumberFormat input. Only re-renders when its own value/error/disabled/readOnly actually change. */
const MemoInputFormat = memo(InputFormat);

/** Drawer-local replacement for the shared FormInputFormat. Same behavior (MMSS time field with
 * blur validation + readOnly support) but built on useField so it can hand the memoized input a
 * stable onChange — that's what lets it bail out of the per-keystroke re-render storm. */
const DurationField = ({ name }) => {
  const [field, meta] = useField(name);
  const { setFieldValue, setFieldTouched, validateForm, isSubmitting, status } = useFormikContext();
  const { t } = useTranslation();
  const [thousand, decimal] = useNumberFormat();
  const { value } = field;

  const separators = useMemo(() => [thousand, decimal], [thousand, decimal]);
  const handleChange = useCallback(
    (next) => next !== value && setFieldValue(name, next),
    [setFieldValue, name, value],
  );

  /** On blur, InputFormat reformats the raw input (e.g. "35" -> "00:35") via onChange/setFieldValue.
   * With validateOnChange disabled (a perf fix), that setFieldValue no longer revalidates, and the
   * field's default blur validation runs against the stale, pre-reformat value — which is what made
   * a valid "21:31" show "enter a valid time". So we mark touched without validating here and defer
   * the validation so it runs after the reformatted value has been committed to the form state.
   * We validate the whole form (not the single field) because Formik's validateField can't update
   * errors for array-nested paths like `cues[0].requestedUseDuration`, which left `isValid` (and
   * thus the Save button) stuck after fixing an invalid value. The timeout (rather than an effect on
   * the value) is needed because the reformat often leaves the value unchanged, so a value-keyed
   * effect wouldn't fire and the stale error would persist. */
  const handleBlur = useCallback(() => {
    setFieldTouched(name, true, false);
    setTimeout(() => validateForm(), 0);
  }, [setFieldTouched, name, validateForm]);

  return (
    <MemoInputFormat
      format={TIME_FORMATS.MMSS}
      type="time"
      name={name}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      separators={separators}
      disabled={isSubmitting}
      readOnly={status === 'readOnly'}
      error={meta.touched && meta.error ? t(meta.error) : undefined}
    />
  );
};

DurationField.propTypes = {
  name: PropTypes.string.isRequired,
};

const CueRow = ({
  name,
  container,
  allCueContainers,
  cueContainersLoading,
  trackId,
  qTrackId,
  refetch,
  hideRemove,
}) => {
  const { values, setFieldValue } = useFormikContext();

  const classes = useStyles();
  const t = useRootTranslation('drawers.clearanceUsesAndDurations');

  const isUseTypeOther = (field) => getIn(values, field) === COMMON_TRACK_USE_TYPES.OTHER;

  const cue = getIn(values, name);
  /** Keep the latest cue in a ref so the memoized ScenesContainer doesn't break on each render. */
  const cueRef = useRef(cue);
  cueRef.current = cue;

  return (
    <>
      <Grid container style={{ marginTop: '1em', marginBottom: '1em' }}>
        <Grid container>
          <Grid item xs={4}>
            <Typography variant="body1">{t('assignToGroup')}</Typography>
          </Grid>
        </Grid>
        <Grid container wrap="nowrap" alignItems="center">
          <Grid item xs>
            {!cueContainersLoading && <ScenesContainer
              cueContainers={allCueContainers}
              currentContainerId={container.id}
              trackId={trackId}
              cueRef={cueRef}
              qTrackId={qTrackId}
              refetch={refetch}
              placeholder={t('selectAGroup')}
            />}
          </Grid>
          <Grid item style={{ width: REMOVE_GUTTER, textAlign: 'center' }}>
            <IconButton
              size="small"
              aria-label={t('removeUsage')}
              onClick={() => setFieldValue(name, { ...cue, deleted: true })}
              style={{ visibility: hideRemove ? 'hidden' : 'visible' }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      <Grid container style={{ paddingRight: REMOVE_GUTTER }}>
        <Grid item xs={6}>
          <Typography variant="body1">{t('reqUseAndDuration')}</Typography>
        </Grid>
        <Grid item xs={6} style={{ paddingLeft: '1em' }}>
          <Typography variant="body1">{t('actUseAndDuration')}</Typography>
        </Grid>
      </Grid>
      <Grid container style={{ paddingRight: REMOVE_GUTTER }}>
        <Grid item xs={6} className={classes.requestedColumn}>
          <Grid container spacing={1} style={{ paddingRight: '1em' }}>
            {!cue.requestedUseDurationFull && (
              <Grid item xs={5}>
                <CustomGridItem className={classes.duration}>
                  <DurationField name={`${name}.requestedUseDuration`} />
                </CustomGridItem>
              </Grid>
            )}
            <Grid item xs={cue.requestedUseDurationFull ? 12 : 7}>
              <CustomGridItem className={classes.usage}>
                <FormTrackUseTypeSelect name={`${name}.requestedUseType`} />
              </CustomGridItem>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container spacing={1} style={{ paddingLeft: '1em' }}>
            <Grid item xs={5}>
              <CustomGridItem className={classes.duration}>
                <DurationField name={`${name}.actualUseDuration`} />
              </CustomGridItem>
            </Grid>
            <Grid item xs={7}>
              <CustomGridItem className={classes.usage}>
                <FormTrackUseTypeSelect name={`${name}.actualUseType`} usageType="actual" />
              </CustomGridItem>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <FormCheckbox name={`${name}.requestedUseDurationFull`} label="Full Use Duration" />
        </Grid>
      </Grid>
      {(isUseTypeOther(`${name}.requestedUseType`) || isUseTypeOther(`${name}.actualUseType`)) && (
        <Grid container style={{ paddingRight: REMOVE_GUTTER }}>
          <Grid item xs={6} style={{ paddingRight: '1em' }}>
            {isUseTypeOther(`${name}.requestedUseType`) && (
              <FormInputText textArea label={t('requestedUsageNotes')} name={`${name}.requestedUsageNotes`} />
            )}
          </Grid>
          <Grid item xs={6} style={{ paddingLeft: '1em' }}>
            {isUseTypeOther(`${name}.actualUseType`) && (
              <FormInputText textArea label={t('actualUsageNotes')} name={`${name}.actualUsageNotes`} />
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

CueRow.propTypes = {
  name: PropTypes.string.isRequired,
  hideRemove: PropTypes.bool,
};

export default enhancer(CueRow);
